import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 30, bottom: 40, left: 30 };
const SVG_WIDTH = 800;
const SVG_HEIGHT = 500;
const HEATMAP_CELL = 28;
const HEATMAP_MARGIN = { top: 50, right: 10, bottom: 10, left: 50 };

// Approximate geographic positions for US states (normalized to 0-1 range)
const STATE_POSITIONS = {
  AL: [0.68, 0.68], AK: [0.12, 0.85], AZ: [0.24, 0.65], AR: [0.55, 0.62],
  CA: [0.10, 0.48], CO: [0.32, 0.48], CT: [0.88, 0.32], DE: [0.84, 0.42],
  FL: [0.76, 0.82], GA: [0.73, 0.68], HI: [0.22, 0.90], ID: [0.20, 0.28],
  IL: [0.60, 0.42], IN: [0.64, 0.42], IA: [0.52, 0.36], KS: [0.44, 0.52],
  KY: [0.68, 0.50], LA: [0.56, 0.76], ME: [0.92, 0.16], MD: [0.82, 0.42],
  MA: [0.90, 0.28], MI: [0.65, 0.30], MN: [0.50, 0.22], MS: [0.62, 0.72],
  MO: [0.54, 0.50], MT: [0.28, 0.18], NE: [0.42, 0.40], NV: [0.16, 0.42],
  NH: [0.90, 0.22], NJ: [0.86, 0.38], NM: [0.28, 0.62], NY: [0.84, 0.28],
  NC: [0.78, 0.55], ND: [0.42, 0.18], OH: [0.70, 0.38], OK: [0.46, 0.60],
  OR: [0.12, 0.25], PA: [0.80, 0.36], RI: [0.90, 0.32], SC: [0.76, 0.62],
  SD: [0.42, 0.26], TN: [0.66, 0.58], TX: [0.42, 0.72], UT: [0.22, 0.44],
  VT: [0.88, 0.18], VA: [0.78, 0.48], WA: [0.14, 0.14], WV: [0.74, 0.44],
  WI: [0.56, 0.26], WY: [0.30, 0.32], DC: [0.82, 0.44],
};

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: { fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '4px' },
  subtitle: { fontSize: '12px', color: '#9ca3af', marginBottom: '20px' },
  chartContainer: {
    overflowX: 'auto',
    position: 'relative',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '280px',
  },
  legend: {
    marginTop: '12px',
    display: 'flex',
    gap: '16px',
    fontSize: '11px',
    color: '#9ca3af',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  legendSwatch: {
    display: 'inline-block',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    verticalAlign: 'middle',
    marginRight: '4px',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
    marginTop: '20px',
  },
  badgeContainer: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: 600,
  },
};

function getNodeColor(state, data) {
  const { states, propagationMatrix, strengthMatrix } = data;
  const idx = states.indexOf(state);
  if (idx === -1) return '#9ca3af';

  // Calculate outgoing strength (contagiousness) and incoming strength (susceptibility)
  let outgoing = 0;
  let incoming = 0;
  let outCount = 0;
  let inCount = 0;

  for (let j = 0; j < states.length; j++) {
    if (j === idx) continue;
    const outStrength = strengthMatrix[idx] && strengthMatrix[idx][j];
    if (outStrength != null) {
      outgoing += outStrength;
      outCount++;
    }
    const inStrength = strengthMatrix[j] && strengthMatrix[j][idx];
    if (inStrength != null) {
      incoming += inStrength;
      inCount++;
    }
  }

  const avgOut = outCount > 0 ? outgoing / outCount : 0;
  const avgIn = inCount > 0 ? incoming / inCount : 0;

  if (state === data.mostContagious) return '#16a34a';
  if (state === data.mostSusceptible) return '#ea580c';

  if (avgOut > avgIn + 0.05) return '#4ade80'; // source-leaning
  if (avgIn > avgOut + 0.05) return '#fb923c'; // susceptible-leaning
  return '#9ca3af'; // neutral
}

function getNodeRadius(state, data) {
  if (state === data.mostContagious || state === data.mostSusceptible) return 18;
  return 14;
}

export default function PriceContagionMap({ data }) {
  const svgRef = useRef(null);
  const heatmapRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [showHeatmap, setShowHeatmap] = useState(false);

  // Main node-link diagram
  useEffect(() => {
    if (!data || !data.states || !data.propagationMatrix || data.states.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { states, propagationMatrix, strengthMatrix, mostContagious, mostSusceptible } = data;
    const n = states.length;

    const innerWidth = SVG_WIDTH - MARGIN.left - MARGIN.right;
    const innerHeight = SVG_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('viewBox', `0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`);

    // Arrowhead marker definition
    const defs = svg.append('defs');

    // Create arrowhead markers for different strengths
    ['low', 'mid', 'high'].forEach((level, i) => {
      const colors = ['#94a3b8', '#3b82f6', '#1e40af'];
      defs.append('marker')
        .attr('id', `arrow-${level}`)
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 20)
        .attr('refY', 0)
        .attr('markerWidth', 6)
        .attr('markerHeight', 6)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,-5L10,0L0,5')
        .attr('fill', colors[i]);
    });

    const g = svg.append('g')
      .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Compute node positions
    const nodePositions = states.map((state) => {
      const pos = STATE_POSITIONS[state];
      if (pos) {
        return {
          state,
          x: pos[0] * innerWidth,
          y: pos[1] * innerHeight,
        };
      }
      // Fallback: hash-based placement
      let hash = 0;
      for (let c = 0; c < state.length; c++) {
        hash = ((hash << 5) - hash) + state.charCodeAt(c);
        hash = hash & hash;
      }
      return {
        state,
        x: (Math.abs(hash % 700) / 700) * innerWidth,
        y: (Math.abs((hash * 31) % 500) / 500) * innerHeight,
      };
    });

    // Build edges from the matrices
    const edges = [];
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        if (i === j) continue;
        const lag = propagationMatrix[i] && propagationMatrix[i][j];
        const strength = strengthMatrix[i] && strengthMatrix[i][j];
        if (lag != null && strength != null) {
          edges.push({
            source: i,
            target: j,
            lag,
            strength,
            sourceState: states[i],
            targetState: states[j],
          });
        }
      }
    }

    // Draw edges (arrows)
    edges.forEach((edge) => {
      const src = nodePositions[edge.source];
      const tgt = nodePositions[edge.target];

      const dx = tgt.x - src.x;
      const dy = tgt.y - src.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 1) return;

      // Offset start and end by node radius
      const srcR = getNodeRadius(src.state, data);
      const tgtR = getNodeRadius(tgt.state, data);
      const ux = dx / dist;
      const uy = dy / dist;
      const x1 = src.x + ux * (srcR + 2);
      const y1 = src.y + uy * (srcR + 2);
      const x2 = tgt.x - ux * (tgtR + 8);
      const y2 = tgt.y - uy * (tgtR + 8);

      // Slight curve for bidirectional clarity
      const mx = (x1 + x2) / 2 - uy * 12;
      const my = (y1 + y2) / 2 + ux * 12;

      // Arrow thickness and color based on strength
      const strokeWidth = 1 + edge.strength * 3;
      let arrowId = 'arrow-low';
      let strokeColor = '#94a3b8';
      if (edge.strength >= 0.7) {
        arrowId = 'arrow-high';
        strokeColor = '#1e40af';
      } else if (edge.strength >= 0.4) {
        arrowId = 'arrow-mid';
        strokeColor = '#3b82f6';
      }

      // Draw the curved arrow
      const path = g.append('path')
        .attr('d', `M${x1},${y1} Q${mx},${my} ${x2},${y2}`)
        .attr('fill', 'none')
        .attr('stroke', strokeColor)
        .attr('stroke-width', strokeWidth)
        .attr('stroke-opacity', 0.5)
        .attr('marker-end', `url(#${arrowId})`)
        .style('cursor', 'pointer');

      // Lag label at midpoint
      const labelX = mx * 0.5 + (x1 + x2) * 0.25;
      const labelY = my * 0.5 + (y1 + y2) * 0.25;

      const lagLabel = g.append('text')
        .attr('x', labelX)
        .attr('y', labelY)
        .attr('text-anchor', 'middle')
        .attr('dy', '-4')
        .attr('font-size', '9px')
        .attr('font-weight', 600)
        .attr('fill', strokeColor)
        .attr('opacity', 0.7)
        .text(`${edge.lag}p`);

      // Hover region for the edge (invisible wider path)
      g.append('path')
        .attr('d', `M${x1},${y1} Q${mx},${my} ${x2},${y2}`)
        .attr('fill', 'none')
        .attr('stroke', 'transparent')
        .attr('stroke-width', Math.max(strokeWidth + 8, 12))
        .style('cursor', 'pointer')
        .on('mouseenter', (event) => {
          path.attr('stroke-opacity', 0.9);
          lagLabel.attr('opacity', 1);
          // Find matching event for direction info
          const matchingEvent = (data.events || []).find(
            (e) => e.sourceState === edge.sourceState && e.targetState === edge.targetState
          );
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            type: 'edge',
            sourceState: edge.sourceState,
            targetState: edge.targetState,
            lag: edge.lag,
            strength: edge.strength,
            direction: matchingEvent ? matchingEvent.direction : null,
            date: matchingEvent ? matchingEvent.date : null,
          });
        })
        .on('mousemove', (event) => {
          setTooltip((prev) => prev ? { ...prev, x: event.clientX + 12, y: event.clientY - 10 } : null);
        })
        .on('mouseleave', () => {
          path.attr('stroke-opacity', 0.5);
          lagLabel.attr('opacity', 0.7);
          setTooltip(null);
        });
    });

    // Draw nodes
    nodePositions.forEach((node, idx) => {
      const color = getNodeColor(node.state, data);
      const radius = getNodeRadius(node.state, data);
      const isMostContagious = node.state === mostContagious;
      const isMostSusceptible = node.state === mostSusceptible;

      // Glow ring for highlighted states
      if (isMostContagious || isMostSusceptible) {
        g.append('circle')
          .attr('cx', node.x)
          .attr('cy', node.y)
          .attr('r', radius + 4)
          .attr('fill', 'none')
          .attr('stroke', isMostContagious ? '#16a34a' : '#ea580c')
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '4,2')
          .attr('opacity', 0.6);
      }

      // Node circle
      g.append('circle')
        .attr('cx', node.x)
        .attr('cy', node.y)
        .attr('r', radius)
        .attr('fill', color)
        .attr('stroke', '#fff')
        .attr('stroke-width', 2)
        .attr('opacity', 0.9)
        .style('cursor', 'pointer');

      // State label
      g.append('text')
        .attr('x', node.x)
        .attr('y', node.y)
        .attr('text-anchor', 'middle')
        .attr('dy', '0.35em')
        .attr('font-size', isMostContagious || isMostSusceptible ? '10px' : '9px')
        .attr('font-weight', 700)
        .attr('fill', '#fff')
        .attr('pointer-events', 'none')
        .text(node.state);

      // Badge for most contagious / most susceptible
      if (isMostContagious) {
        g.append('text')
          .attr('x', node.x)
          .attr('y', node.y - radius - 6)
          .attr('text-anchor', 'middle')
          .attr('font-size', '12px')
          .attr('fill', '#16a34a')
          .text('\u2605');
      }
      if (isMostSusceptible) {
        g.append('text')
          .attr('x', node.x)
          .attr('y', node.y - radius - 6)
          .attr('text-anchor', 'middle')
          .attr('font-size', '11px')
          .attr('fill', '#ea580c')
          .attr('font-weight', 700)
          .text('\u25CE');
      }

      // Hover region for node
      g.append('circle')
        .attr('cx', node.x)
        .attr('cy', node.y)
        .attr('r', radius + 4)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mouseenter', (event) => {
          // Calculate outgoing and incoming stats
          let outLinks = 0, inLinks = 0;
          let avgOutStrength = 0, avgInStrength = 0;
          let avgOutLag = 0, avgInLag = 0;
          for (let j = 0; j < n; j++) {
            if (j === idx) continue;
            const outS = strengthMatrix[idx] && strengthMatrix[idx][j];
            const outL = propagationMatrix[idx] && propagationMatrix[idx][j];
            if (outS != null && outL != null) {
              outLinks++;
              avgOutStrength += outS;
              avgOutLag += outL;
            }
            const inS = strengthMatrix[j] && strengthMatrix[j][idx];
            const inL = propagationMatrix[j] && propagationMatrix[j][idx];
            if (inS != null && inL != null) {
              inLinks++;
              avgInStrength += inS;
              avgInLag += inL;
            }
          }
          if (outLinks > 0) { avgOutStrength /= outLinks; avgOutLag /= outLinks; }
          if (inLinks > 0) { avgInStrength /= inLinks; avgInLag /= inLinks; }

          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            type: 'node',
            state: node.state,
            isMostContagious,
            isMostSusceptible,
            outLinks,
            inLinks,
            avgOutStrength: avgOutStrength.toFixed(2),
            avgInStrength: avgInStrength.toFixed(2),
            avgOutLag: avgOutLag.toFixed(1),
            avgInLag: avgInLag.toFixed(1),
          });
        })
        .on('mousemove', (event) => {
          setTooltip((prev) => prev ? { ...prev, x: event.clientX + 12, y: event.clientY - 10 } : null);
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Mini heatmap of the propagation matrix
  useEffect(() => {
    if (!showHeatmap || !data || !data.states || !data.propagationMatrix || data.states.length === 0) return;

    const svg = d3.select(heatmapRef.current);
    svg.selectAll('*').remove();

    const { states, propagationMatrix, strengthMatrix } = data;
    const n = states.length;

    const width = HEATMAP_MARGIN.left + n * HEATMAP_CELL + HEATMAP_MARGIN.right;
    const height = HEATMAP_MARGIN.top + n * HEATMAP_CELL + HEATMAP_MARGIN.bottom;

    svg.attr('viewBox', `0 0 ${width} ${height}`);

    const g = svg.append('g')
      .attr('transform', `translate(${HEATMAP_MARGIN.left}, ${HEATMAP_MARGIN.top})`);

    // Find max lag for color scaling
    let maxLag = 0;
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        const val = propagationMatrix[i] && propagationMatrix[i][j];
        if (val != null && val > maxLag) maxLag = val;
      }
    }

    const lagColorScale = d3.scaleSequential()
      .domain([0, Math.max(maxLag, 1)])
      .interpolator(d3.interpolateBlues);

    // Column headers (rotated)
    states.forEach((state, ci) => {
      g.append('text')
        .attr('x', ci * HEATMAP_CELL + HEATMAP_CELL / 2)
        .attr('y', -6)
        .attr('text-anchor', 'start')
        .attr('transform', `rotate(-45, ${ci * HEATMAP_CELL + HEATMAP_CELL / 2}, ${-6})`)
        .attr('font-size', '9px')
        .attr('font-weight', 600)
        .attr('fill', state === data.mostContagious ? '#16a34a' : state === data.mostSusceptible ? '#ea580c' : '#6b7280')
        .text(state);
    });

    // Row labels and cells
    states.forEach((rowState, ri) => {
      g.append('text')
        .attr('x', -6)
        .attr('y', ri * HEATMAP_CELL + HEATMAP_CELL / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'end')
        .attr('font-size', '9px')
        .attr('font-weight', 600)
        .attr('fill', rowState === data.mostContagious ? '#16a34a' : rowState === data.mostSusceptible ? '#ea580c' : '#6b7280')
        .text(rowState);

      states.forEach((colState, ci) => {
        const isDiag = ri === ci;
        const lag = propagationMatrix[ri] && propagationMatrix[ri][ci];
        const strength = strengthMatrix[ri] && strengthMatrix[ri][ci];

        const cellX = ci * HEATMAP_CELL;
        const cellY = ri * HEATMAP_CELL;

        if (isDiag) {
          g.append('rect')
            .attr('x', cellX + 1)
            .attr('y', cellY + 1)
            .attr('width', HEATMAP_CELL - 2)
            .attr('height', HEATMAP_CELL - 2)
            .attr('rx', 2)
            .attr('fill', '#f3f4f6')
            .attr('stroke', '#e5e7eb')
            .attr('stroke-width', 0.5);
        } else if (lag == null) {
          g.append('rect')
            .attr('x', cellX + 1)
            .attr('y', cellY + 1)
            .attr('width', HEATMAP_CELL - 2)
            .attr('height', HEATMAP_CELL - 2)
            .attr('rx', 2)
            .attr('fill', '#f9fafb')
            .attr('stroke', '#e5e7eb')
            .attr('stroke-width', 0.5);

          g.append('text')
            .attr('x', cellX + HEATMAP_CELL / 2)
            .attr('y', cellY + HEATMAP_CELL / 2)
            .attr('dy', '0.35em')
            .attr('text-anchor', 'middle')
            .attr('font-size', '8px')
            .attr('fill', '#d1d5db')
            .text('--');
        } else {
          const fillColor = lagColorScale(maxLag - lag); // invert: low lag = dark
          g.append('rect')
            .attr('x', cellX + 1)
            .attr('y', cellY + 1)
            .attr('width', HEATMAP_CELL - 2)
            .attr('height', HEATMAP_CELL - 2)
            .attr('rx', 2)
            .attr('fill', fillColor)
            .attr('stroke', '#e5e7eb')
            .attr('stroke-width', 0.5);

          g.append('text')
            .attr('x', cellX + HEATMAP_CELL / 2)
            .attr('y', cellY + HEATMAP_CELL / 2)
            .attr('dy', '0.35em')
            .attr('text-anchor', 'middle')
            .attr('font-size', '8px')
            .attr('font-weight', 600)
            .attr('fill', lag <= maxLag * 0.4 ? '#fff' : '#1e3a5f')
            .text(lag.toFixed(1));
        }

        // Hover for heatmap cells
        if (!isDiag) {
          g.append('rect')
            .attr('x', cellX)
            .attr('y', cellY)
            .attr('width', HEATMAP_CELL)
            .attr('height', HEATMAP_CELL)
            .attr('fill', 'transparent')
            .style('cursor', lag != null ? 'pointer' : 'default')
            .on('mouseenter', (event) => {
              setTooltip({
                x: event.clientX + 12,
                y: event.clientY - 10,
                type: 'heatmap',
                sourceState: rowState,
                targetState: colState,
                lag,
                strength,
              });
            })
            .on('mousemove', (event) => {
              setTooltip((prev) => prev ? { ...prev, x: event.clientX + 12, y: event.clientY - 10 } : null);
            })
            .on('mouseleave', () => {
              setTooltip(null);
            });
        }
      });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, showHeatmap]);

  // Empty state
  if (!data || !data.states || !data.propagationMatrix || data.states.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Contagion Map</div>
        <div style={styles.subtitle}>Cross-state price change propagation patterns and lag structure</div>
        <div style={styles.emptyState}>No contagion data available</div>
      </div>
    );
  }

  const { mostContagious, mostSusceptible } = data;

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Contagion Map</div>
      <div style={styles.subtitle}>Cross-state price change propagation patterns and lag structure</div>

      {/* Badges for most contagious and most susceptible */}
      <div style={styles.badgeContainer}>
        {mostContagious && (
          <span style={{
            ...styles.badge,
            background: '#f0fdf4',
            color: '#16a34a',
            border: '1px solid #bbf7d0',
          }}>
            {'\u2605'} Most Contagious: {mostContagious}
          </span>
        )}
        {mostSusceptible && (
          <span style={{
            ...styles.badge,
            background: '#fff7ed',
            color: '#ea580c',
            border: '1px solid #fed7aa',
          }}>
            {'\u25CE'} Most Susceptible: {mostSusceptible}
          </span>
        )}
      </div>

      {/* Main node-link diagram */}
      <div style={styles.chartContainer}>
        <svg
          ref={svgRef}
          style={{ width: '100%', height: 'auto' }}
          preserveAspectRatio="xMidYMid meet"
        />
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <span style={{ fontWeight: 600, color: '#6b7280' }}>Nodes:</span>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#16a34a' }} />
          Source (contagious)
        </span>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#ea580c' }} />
          Susceptible
        </span>
        <span>
          <span style={{ ...styles.legendSwatch, background: '#9ca3af' }} />
          Neutral
        </span>
        <span style={{ marginLeft: '12px', fontWeight: 600, color: '#6b7280' }}>Arrows:</span>
        <span>
          Thickness = strength, label = lag (periods)
        </span>
      </div>

      {/* Toggle for mini heatmap */}
      <div style={{ marginTop: '16px' }}>
        <button
          onClick={() => setShowHeatmap(!showHeatmap)}
          style={{
            background: showHeatmap ? '#eff6ff' : '#f9fafb',
            border: '1px solid ' + (showHeatmap ? '#bfdbfe' : '#e5e7eb'),
            borderRadius: '6px',
            padding: '6px 14px',
            fontSize: '12px',
            fontWeight: 600,
            color: showHeatmap ? '#1e40af' : '#6b7280',
            cursor: 'pointer',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          }}
        >
          {showHeatmap ? 'Hide' : 'Show'} Propagation Matrix
        </button>
      </div>

      {/* Mini heatmap */}
      {showHeatmap && (
        <div style={{ marginTop: '12px' }}>
          <div style={styles.sectionTitle}>Propagation Lag Matrix (periods)</div>
          <div style={styles.chartContainer}>
            <svg
              ref={heatmapRef}
              style={{ width: '100%', maxWidth: '600px', height: 'auto' }}
              preserveAspectRatio="xMidYMid meet"
            />
          </div>
        </div>
      )}

      {/* Tooltip */}
      {tooltip && tooltip.type === 'node' && (
        <div style={{ ...styles.tooltip, left: tooltip.x, top: tooltip.y }}>
          <div style={{ fontWeight: 700, color: '#1f2937', marginBottom: '6px', fontSize: '13px' }}>
            {tooltip.state}
            {tooltip.isMostContagious && (
              <span style={{ marginLeft: '6px', color: '#16a34a', fontSize: '11px' }}>{'\u2605'} Most Contagious</span>
            )}
            {tooltip.isMostSusceptible && (
              <span style={{ marginLeft: '6px', color: '#ea580c', fontSize: '11px' }}>{'\u25CE'} Most Susceptible</span>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '3px' }}>
            <span style={{ color: '#6b7280' }}>Outgoing links</span>
            <span style={{ fontWeight: 600, color: '#374151' }}>{tooltip.outLinks}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '3px' }}>
            <span style={{ color: '#6b7280' }}>Avg out strength</span>
            <span style={{ fontWeight: 600, color: '#374151' }}>{tooltip.avgOutStrength}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '3px' }}>
            <span style={{ color: '#6b7280' }}>Avg out lag</span>
            <span style={{ fontWeight: 600, color: '#374151' }}>{tooltip.avgOutLag}p</span>
          </div>
          <div style={{ borderTop: '1px solid #f3f4f6', marginTop: '4px', paddingTop: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '3px' }}>
              <span style={{ color: '#6b7280' }}>Incoming links</span>
              <span style={{ fontWeight: 600, color: '#374151' }}>{tooltip.inLinks}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '3px' }}>
              <span style={{ color: '#6b7280' }}>Avg in strength</span>
              <span style={{ fontWeight: 600, color: '#374151' }}>{tooltip.avgInStrength}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#6b7280' }}>Avg in lag</span>
              <span style={{ fontWeight: 600, color: '#374151' }}>{tooltip.avgInLag}p</span>
            </div>
          </div>
        </div>
      )}
      {tooltip && tooltip.type === 'edge' && (
        <div style={{ ...styles.tooltip, left: tooltip.x, top: tooltip.y }}>
          <div style={{ fontWeight: 700, color: '#1f2937', marginBottom: '6px' }}>
            {tooltip.sourceState} &rarr; {tooltip.targetState}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '3px' }}>
            <span style={{ color: '#6b7280' }}>Propagation lag</span>
            <span style={{ fontWeight: 600, color: '#374151' }}>{tooltip.lag} period{tooltip.lag !== 1 ? 's' : ''}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '3px' }}>
            <span style={{ color: '#6b7280' }}>Strength</span>
            <span style={{ fontWeight: 600, color: '#374151' }}>{(tooltip.strength * 100).toFixed(0)}%</span>
          </div>
          {tooltip.direction && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '3px' }}>
              <span style={{ color: '#6b7280' }}>Direction</span>
              <span style={{
                fontWeight: 600,
                color: tooltip.direction === 'increase' ? '#16a34a' : '#dc2626',
              }}>
                {tooltip.direction === 'increase' ? '\u2191 Increase' : '\u2193 Decrease'}
              </span>
            </div>
          )}
          {tooltip.date && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#6b7280' }}>Date</span>
              <span style={{ fontWeight: 600, color: '#374151' }}>{tooltip.date}</span>
            </div>
          )}
          <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '6px', paddingTop: '6px', fontSize: '11px', color: '#6b7280' }}>
            Price changes in {tooltip.sourceState} propagate to {tooltip.targetState} after ~{tooltip.lag} period{tooltip.lag !== 1 ? 's' : ''} with {(tooltip.strength * 100).toFixed(0)}% correlation
          </div>
        </div>
      )}
      {tooltip && tooltip.type === 'heatmap' && (
        <div style={{ ...styles.tooltip, left: tooltip.x, top: tooltip.y }}>
          <div style={{ fontWeight: 700, color: '#1f2937', marginBottom: '6px' }}>
            {tooltip.sourceState} &rarr; {tooltip.targetState}
          </div>
          {tooltip.lag != null ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '3px' }}>
                <span style={{ color: '#6b7280' }}>Lag</span>
                <span style={{ fontWeight: 600, color: '#374151' }}>{tooltip.lag} period{tooltip.lag !== 1 ? 's' : ''}</span>
              </div>
              {tooltip.strength != null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                  <span style={{ color: '#6b7280' }}>Strength</span>
                  <span style={{ fontWeight: 600, color: '#374151' }}>{(tooltip.strength * 100).toFixed(0)}%</span>
                </div>
              )}
            </>
          ) : (
            <div style={{ color: '#9ca3af', fontStyle: 'italic' }}>No significant relationship</div>
          )}
        </div>
      )}
    </div>
  );
}
